/* eslint-disable indent */
import { html, css, LitElement } from 'lit';
import { customElement, state, query } from 'lit/decorators.js';
import { connect } from 'pwa-helpers/connect-mixin.js';
import { store } from '../../store/store.js';
import sharedStyles from '../../app/styles.js';
import {
    loadYoutubeItemsIfNeeded,
    publishYoutubeItemAsComposition,
    unpublishYoutubeItemAsComposition,
    createYoutubeItem,
    deleteYoutubeItem,
} from '../../store/youtube/youtube.js';
import { previewElearning } from '../../store/preview/preview.js';
import { navigate } from '../../store/route/route.js';
import {
    extractYoutubeData,
    fetchYoutubeMetadata,
} from '../../store/youtube/helpers.js';
import { createSupplierKey } from '../../store/elearning/helpers.js'
import { translate } from '../../helpers/translate.js';
import { renderTimestamp } from '../../helpers/controls.js';
import '@lit-labs/virtualizer';
import '@polymer/app-layout/app-layout.js';
import '@vaadin/context-menu/theme/material/vaadin-context-menu.js';
import '@material/web/fab/fab.js';
import '@sdb-develop/learning-frontend';
import '../shared/sdb-search-input.js';
import '../shared/sdb-loading.js';
import '../shared/sdb-status-message.js';
import '../shared/sdb-dialog.js';

@customElement('sdb-youtube-item-list')
class SdbYoutubeItemList extends connect(store)(LitElement) {

    @state()
    accessor youtubeItems = [];

    @state()
    accessor total;

    @state()
    accessor search;

    @state()
    accessor isLoadingFirstTime;

    @state()
    accessor user;

    @state()
    accessor args;

    @state()
    accessor newPackage = null;

    @state()
    accessor youtubeUrl = null;

    @query('#youtube_uploader')
    accessor uploader;

    stateChanged(state) {
        this.youtubeItems = state.youtube.list || [];
        this.total = state.youtube.total;
        this.search = state.youtube.search;
        this.isLoadingFirstTime = state.youtube.status === 'loading' && state.youtube.page === -1;
        this.user = state.auth.user;
        this.portalId = state.env.portalIdentifier;
        this.hasGlobalId = state.env.hasGlobalId;
    }

    get urlInput() {
        return document.getElementById('url_input'); // it's in a dialog, so not in the hosts dom
    }

    static styles = [
        sharedStyles,
        css`
            :host {
                max-width: var(--tcg-content-max-width);
                width: 100%;
            }

            .right-nav-bar {
                display:flex;
                align-items: center;
                justify-content: space-between;
            }

            sdb-search-input {
                flex-grow: 1;
            }

            sdb-content-iconbutton {
                margin-left: 10px;
            }

            #youtube_items_list {
                contain: none !important; /* lit-virtualizer hack */
            }

            sdb-content-listitem {
                width: 100%; /* lit-virtualizer screws this up */
            }

            paper-item {
                cursor: pointer;
            }

            paper-icon-button {
                color: var(--icon-button-color);
            }

            paper-listbox {
                min-width: 150px;
            }

            .no-results {
                width: 100%;
                max-width: var(--tcg-content-max-width);
                height: 100%;
                display: flex;
                flex-direction: column;
                align-items: center;
            }

            #no_results_img {
                width: 400px;
                height: 400px;
                background-color: var(--tcg-secondary-background-color);
            }
        `,
    ];

    render() {
        if (this.isLoadingFirstTime) {
            return html`<sdb-loading></sdb-loading>`;
        }

        return html`
            <sdb-layout-60-40>
                <sdb-nav-titlebar slot="left">
                    <span>${translate('headerYoutubeItems')}</span>
                    <span ?hidden=${!this.total}>&nbsp;(${this.total})</span>
                </sdb-nav-titlebar>
                <div class="right-nav-bar" slot="right">
                    <sdb-search-input
                        placeholder=${translate('search')}
                        value=${this.search}
                        @search=${(e) => { store.dispatch(loadYoutubeItemsIfNeeded({ search: e.detail.searchText, reload: true })); }}>
                    </sdb-search-input>
                </div>
            </sdb-layout-60-40>
            <sdb-layout-100>
                ${!this.total
                    ? html`<sdb-status-message label=${translate('noYoutubeItemsFound')}></sdb-status-message>`
                    : html`
                        <sdb-content-list separator>
                            <lit-virtualizer
                                id="youtube_items_list"
                                .items=${this.youtubeItems}
                                .renderItem=${(youtubeItem, index) => this._renderYoutubeItemListItem(youtubeItem, index)}
                                .scrollTarget=${document}>
                            </lit-virtualizer>
                        </sdb-content-list>
                    `
                }

                <md-fab @click=${()=> { this._openDialog('add', null); }}>
                    <sdb-content-icon
                        slot="icon"
                        icon="fal fa-plus"
                        size="24">
                    </sdb-content-icon>
                </md-fab>
            </sdb-layout-100>

            <sdb-dialog
                type="confirm"
                id="publish_dialog"
                heading=${translate('publishHeadingYoutubeItem')}
                body=${translate('publishBodyYoutubeItem')}
                confirmCaption=${translate('publish')}
                @confirm=${(e) => { store.dispatch(publishYoutubeItemAsComposition({ item: e.detail.args })); }}>
            </sdb-dialog>

            <sdb-dialog
                type="confirm"
                id="unpublish_dialog"
                heading=${translate('unpublishHeadingYoutubeItem')}
                body=${translate('unpublishBodyYoutubeItem')}
                confirmCaption=${translate('unpublish')}
                @confirm=${(e) => { store.dispatch(unpublishYoutubeItemAsComposition({ item: e.detail.args })); }}>
            </sdb-dialog>

            <sdb-dialog
                type="confirm"
                id="delete_dialog"
                heading=${translate('deleteHeadingYoutubeItem')}
                confirmCaption=${translate('delete')}
                @confirm=${(e) => { store.dispatch(deleteYoutubeItem({ id: e.detail.args })); }}>
            </sdb-dialog>

            <sdb-dialog
                type="form"
                id="add_dialog"
                heading=${translate('createHeadingYoutubeItem')}
                confirmCaption=${translate('create')}
                ?confirmDisabled=${!this.youtubeUrl}
                @confirm=${async () => {
                    await this._createYoutubeItem(this.youtubeUrl);
                    this.youtubeUrl = '';
                    this.urlInput.value = '';
                }}
                .bodyRenderer=${() => html`
                    <paper-input
                        id="url_input"
                        transparent
                        style="width: 700px;"
                        label=${translate('url')}
                        @value-changed=${(e) => { this.youtubeUrl = e.target.value; }}>
                    </paper-input>
                    <span style="display: block; margin-top: 4px; font-size: 12px; color: rgba(0, 0, 0, 0.6);">${translate('createHeadingYoutubeItemHelper')}</span>
                `}>
            </sdb-dialog>
        `;
    }

    _renderYoutubeItemListItem(youtubeItem, index) {
        store.dispatch(loadYoutubeItemsIfNeeded({ index }));

        const menuItems = [
            {
                text: translate('publish'),
                action: () => this._openDialog('publish', youtubeItem),
                disabled: youtubeItem.elearningType === 0,
                hidden: this.hasGlobalId,
            },
            {
                text: translate('unpublish'),
                action: () => this._openDialog('unpublish', youtubeItem),
                disabled: youtubeItem.elearningType === 1,
                hidden: this.hasGlobalId,
            },
            {
                text: translate('preview'),
                action: () => store.dispatch(previewElearning({ item: youtubeItem })),
            },
        ].filter(item => !item.hidden);

        return html`
            <sdb-content-listitem
                icon="fal fa-video"
                .graphic=${youtubeItem.thumbnail}
                .headline=${youtubeItem.title}
                .subtitle=${this._getSubtitle(youtubeItem)}
                @click=${()=> { store.dispatch(navigate(`youtubeitem/${youtubeItem.targetId}`)); }}>

                <sdb-content-chipbar slot="chipbar">
                    <sdb-content-chip label="catalogus" ?hidden=${youtubeItem.elearningType !== 0}></sdb-content-chip>
                </sdb-content-chipbar>

                <vaadin-context-menu
                    slot="secondaryAction"
                    open-on="click"
                    .items=${menuItems}
                    @item-selected=${(e) => {
                        e.stopPropagation();
                        e.detail.value?.action?.();
                    }}>
                    <sdb-content-iconbutton icon="fal fa-ellipsis-vertical"></sdb-content-iconbutton>
                </vaadin-context-menu>

            </sdb-content-listitem>
        `;
    }

    _getSubtitle(youtubeItem) {
        var s = `${translate('lastModified')}: ${renderTimestamp(youtubeItem.lastModified)}`;
        if (youtubeItem.supplier && youtubeItem.supplier.supplierKey) {
            s += ` | ${youtubeItem.supplier.name}`;
        }
        if (youtubeItem.description) {
            s += ` | ${youtubeItem.description}`;
        }
        return s;
    }

    _openDialog(action, args) {
        this.renderRoot.querySelector(`#${action}_dialog`).open(args);
    }

    async _createYoutubeItem(url) {
        const {
            videoId,
            startAt,
            endAt,
        } = extractYoutubeData(url);
        if (!videoId)
            return;

        const {
            title,
            author_name,
            thumbnail_url,
        } = await fetchYoutubeMetadata(videoId);

        store.dispatch(createYoutubeItem({
            name: title,
            supplier: {
                name: author_name,
                supplierKey: createSupplierKey(author_name),
            },
            image: thumbnail_url,
            thumbnail: thumbnail_url,
            startData: {
                url: url,
                videoId: videoId,
                autoplay: false,
                mute: false,
                startAt: startAt,
                endAt: endAt,
                thumbnailUrl: thumbnail_url,
            },
            autoComplete: true,
        }));
    }
}

